import { api, throwApiError } from "../../../api/api";

/**
 * Lado do DP sobre o acesso do colaborador ao app.
 *
 * Usa o `api` do staff — quem chama estas rotas é uma pessoa do DP ou um ADMIN,
 * não o colaborador. O portal tem instância própria (`employeeApi`).
 */

export type DpCredentialEventType =
  | "PROVISIONADA"
  | "SENHA_PROVISORIA_REEMITIDA"
  | "SENHA_ALTERADA"
  | "BLOQUEADA_POR_TENTATIVAS"
  | "DESBLOQUEADA"
  | "DESATIVADA"
  | "REATIVADA";

export const CREDENTIAL_EVENT_LABELS: Record<DpCredentialEventType, string> = {
  PROVISIONADA: "Acesso criado",
  SENHA_PROVISORIA_REEMITIDA: "Senha provisória reemitida",
  SENHA_ALTERADA: "Senha alterada pelo colaborador",
  BLOQUEADA_POR_TENTATIVAS: "Bloqueado por tentativas erradas",
  DESBLOQUEADA: "Desbloqueado",
  DESATIVADA: "Acesso desativado",
  REATIVADA: "Acesso reativado",
};

export type DpCredentialEvent = {
  id: string;
  type: DpCredentialEventType;
  note: string | null;
  createdAt: string;
  actorUser: { id: string; nomeCompleto: string } | null;
};

export type DpCredentialStatus = {
  exists: boolean;
  id?: string;
  isFirstLogin?: boolean;
  lastLoginAt?: string | null;
  lockedUntil?: string | null;
  isLocked?: boolean;
  failedAttempts?: number;
  disabledAt?: string | null;
  disabledReason?: string | null;
  passwordChangedAt?: string | null;
  createdAt?: string;
  events: DpCredentialEvent[];
};

/** A senha provisória em claro vem SÓ nesta resposta — depois só existe o hash. */
export type DpCredentialWithPassword = DpCredentialStatus & {
  provisionalPassword: string;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const getCredentialStatusApi = async (
  token: string,
  employeeId: string
): Promise<DpCredentialStatus> => {
  try {
    const { data } = await api.get<DpCredentialStatus>(
      `/dp/employees/${employeeId}/credential`,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a situação do acesso.");
    throw error;
  }
};

/**
 * Cria o acesso. `provisionalPassword` é opcional: informada, é a senha que o DP
 * escolheu; omitida, o backend gera uma. Em ambos os casos a senha volta em
 * claro nesta resposta — a única vez.
 */
export const provisionCredentialApi = async (
  token: string,
  employeeId: string,
  provisionalPassword?: string
): Promise<DpCredentialWithPassword> => {
  try {
    const { data } = await api.post<DpCredentialWithPassword>(
      `/dp/employees/${employeeId}/credential`,
      provisionalPassword ? { provisionalPassword } : {},
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar o acesso.");
    throw error;
  }
};

export const resetCredentialApi = async (
  token: string,
  employeeId: string,
  provisionalPassword?: string
): Promise<DpCredentialWithPassword> => {
  try {
    const { data } = await api.post<DpCredentialWithPassword>(
      `/dp/employees/${employeeId}/credential/reset`,
      provisionalPassword ? { provisionalPassword } : {},
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao reemitir a senha.");
    throw error;
  }
};

/** Mínimo da senha provisória — espelha a regra do backend. */
export const MIN_PROVISIONAL_PASSWORD_LENGTH = 6;

export const disableCredentialApi = async (
  token: string,
  employeeId: string,
  reason?: string
): Promise<DpCredentialStatus> => {
  try {
    const { data } = await api.post<DpCredentialStatus>(
      `/dp/employees/${employeeId}/credential/disable`,
      reason ? { reason } : {},
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desativar o acesso.");
    throw error;
  }
};

export const reactivateCredentialApi = async (
  token: string,
  employeeId: string
): Promise<DpCredentialStatus> => {
  try {
    const { data } = await api.post<DpCredentialStatus>(
      `/dp/employees/${employeeId}/credential/reactivate`,
      {},
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao reativar o acesso.");
    throw error;
  }
};
